import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Gift, Users, CheckCircle, XCircle, Clock, ArrowLeft,
  Settings, Save, Power, PowerOff
} from 'lucide-react';

export default function AdminFreeRaffle() {
  const [settings, setSettings] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editSettings, setEditSettings] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, participantsRes] = await Promise.all([
        api.get('/free-raffle/settings'),
        api.get('/admin/free-raffle/participants')
      ]);
      setSettings(settingsRes.data);
      setEditSettings({
        name: settingsRes.data.name || '',
        description: settingsRes.data.description || '',
        prize_major: settingsRes.data.prize_major || 500000,
        prize_inverted: settingsRes.data.prize_inverted || 300000,
        prize_last_two: settingsRes.data.prize_last_two || 150000,
        prize_first_two: settingsRes.data.prize_first_two || 50000,
        total_tickets: settingsRes.data.total_tickets || 200
      });
      setParticipants(participantsRes.data.participants || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const toggleEnabled = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/free-raffle/settings', null, {
        params: { enabled: !settings.enabled }
      });
      toast.success(settings.enabled ? 'Sorteo gratuito desactivado' : 'Sorteo gratuito activado');
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/free-raffle/settings', null, {
        params: editSettings
      });
      toast.success('Configuración guardada');
      fetchData();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (participantId, action, ticketsCount = 1) => {
    try {
      await api.patch(`/admin/free-raffle/validate/${participantId}`, null, {
        params: { action, tickets_count: ticketsCount }
      });
      toast.success(action === 'validate' ? 'Participante validado' : 'Participante rechazado');
      fetchData();
    } catch (error) {
      toast.error('Error al procesar');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const filteredParticipants = statusFilter === 'all' 
    ? participants 
    : participants.filter(p => p.status === statusFilter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Gift className="w-6 h-6" />
                Sorteo Gratuito
              </h1>
              <p className="text-purple-100 text-sm">Gestión del sorteo gratuito</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Toggle Button */}
        <Card className={settings?.enabled ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {settings?.enabled ? (
                <Power className="w-8 h-8 text-green-600" />
              ) : (
                <PowerOff className="w-8 h-8 text-red-600" />
              )}
              <div>
                <h3 className="font-bold">
                  {settings?.enabled ? 'Sorteo ACTIVO' : 'Sorteo INACTIVO'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {settings?.enabled 
                    ? 'Los usuarios pueden postularse' 
                    : 'Los usuarios NO pueden postularse'}
                </p>
              </div>
            </div>
            <Button
              onClick={toggleEnabled}
              disabled={saving}
              variant={settings?.enabled ? 'destructive' : 'default'}
              className={!settings?.enabled ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {settings?.enabled ? 'Desactivar' : 'Activar'}
            </Button>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-green-50">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-2xl font-bold text-green-700">
                {participants.filter(p => p.status === 'validated').length}
              </p>
              <p className="text-xs text-green-600">Validados</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto text-amber-600 mb-2" />
              <p className="text-2xl font-bold text-amber-700">
                {participants.filter(p => p.status === 'pending').length}
              </p>
              <p className="text-xs text-amber-600">Pendientes</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="p-4 text-center">
              <XCircle className="w-8 h-8 mx-auto text-red-600 mb-2" />
              <p className="text-2xl font-bold text-red-700">
                {participants.filter(p => p.status === 'rejected').length}
              </p>
              <p className="text-xs text-red-600">Rechazados</p>
            </CardContent>
          </Card>
        </div>

        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuración de Premios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Premio Mayor</Label>
                <Input
                  type="number"
                  value={editSettings.prize_major}
                  onChange={(e) => setEditSettings({...editSettings, prize_major: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label>Número Invertido</Label>
                <Input
                  type="number"
                  value={editSettings.prize_inverted}
                  onChange={(e) => setEditSettings({...editSettings, prize_inverted: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label>Últimas 2 cifras</Label>
                <Input
                  type="number"
                  value={editSettings.prize_last_two}
                  onChange={(e) => setEditSettings({...editSettings, prize_last_two: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label>Primeras 2 cifras</Label>
                <Input
                  type="number"
                  value={editSettings.prize_first_two}
                  onChange={(e) => setEditSettings({...editSettings, prize_first_two: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <Label>Total de Tickets</Label>
              <Input
                type="number"
                value={editSettings.total_tickets}
                onChange={(e) => setEditSettings({...editSettings, total_tickets: parseInt(e.target.value)})}
              />
            </div>
            <Button onClick={saveSettings} disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              Guardar Configuración
            </Button>
          </CardContent>
        </Card>

        {/* Participants */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Participantes ({filteredParticipants.length})
              </CardTitle>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="all">Todos</option>
                <option value="pending">Pendientes</option>
                <option value="validated">Validados</option>
                <option value="rejected">Rechazados</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {filteredParticipants.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No hay participantes en esta categoría
              </p>
            ) : (
              <div className="space-y-3">
                {filteredParticipants.map((participant) => (
                  <div 
                    key={participant.id}
                    className={`p-4 rounded-lg border ${
                      participant.status === 'validated' ? 'bg-green-50 border-green-200' :
                      participant.status === 'pending' ? 'bg-amber-50 border-amber-200' :
                      'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold">{participant.user_name}</p>
                        <p className="text-sm text-muted-foreground">{participant.user_email}</p>
                        <p className="text-sm text-muted-foreground">{participant.user_phone}</p>
                        {participant.ticket_numbers?.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {participant.ticket_numbers.map(num => (
                              <span key={num} className="px-2 py-1 bg-primary text-white rounded text-xs font-mono">
                                {num}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {participant.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleValidate(participant.id, 'validate')}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Validar
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleValidate(participant.id, 'reject')}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
